// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

// A non-recursive subset of CodeableConcept used in form state.
// Avoids the TypeScript TS2589 "type instantiation too deep" error caused by
// CodeableConcept → Coding → Extension → Extension (recursive) when Mantine's
// UseFormReturnType generates path union types.
export type FormCodeableConcept = {
  coding?: Array<{ system?: string; code?: string; display?: string; version?: string; userSelected?: boolean }>;
  text?: string;
};

export const NOTE_CODES = [
  'assessment',
  'hpi',
  'treatment-plan',
  'vital-signs',
  'review-symptoms',
  'exam',
  'patient-note',
  'referring-note',
] as const;
export type NoteCode = (typeof NOTE_CODES)[number];

export interface LoadedNoteObservations {
  assessment: { id?: string; text: string };
  hpi: { id?: string; text: string };
  'treatment-plan': { id?: string; text: string };
  'vital-signs': { id?: string; text: string };
  'review-symptoms': { id?: string; text: string };
  exam: { id?: string; text: string };
  'patient-note': { id?: string; text: string };
  'referring-note': { id?: string; text: string };
}

export interface MedicationFormRow {
  id?: string;
  medicationConcept: FormCodeableConcept | undefined;
  dosage: string;
  unit: string;
  frequency: string;
  dateStarted: string | null;
}

export interface SymptomFormRow {
  id?: string;
  symptom: string;
  severity: string;
  frequency: string;
  onset: string | null;
  snomedConcept: FormCodeableConcept | undefined;
}

export interface ComorbidityFormRow {
  id?: string;
  icd10Concept: FormCodeableConcept | undefined;
  onset: string | null;
}

export interface DiagnosisFormValue {
  id?: string;
  concept: FormCodeableConcept | undefined;
  recordedDate?: string;
}

export const MIN_DIAGNOSIS_ENTRIES = 2;

export interface ChartFormValues {
  startTime: string;
  endTime: string;
  treatmentPlan: string;
  vitalSigns: string;
  reviewSymptoms: string;
  exam: string;
  noteForPatient: string;
  noteForReferring: string;
  chiefComplaint: string;
  assessment: string;
  hpi: string;
  medications: MedicationFormRow[];
  symptoms: SymptomFormRow[];
  comorbidities: ComorbidityFormRow[];
  diagnoses: DiagnosisFormValue[];
  suspectedPositionalApnea: string;
}

export interface EncounterNoteConditionRow {
  clientId: string;
  id?: string;
  condition: string;
  code: string;
  onsetDate: string | null;
  medicationIds: string[];
  concept?: FormCodeableConcept;
  categoryCode?: 'symptom' | 'comorbidity';
  sourceDate?: string;
  confirmedAt?: string;
  confirmedBy?: string;
}

export interface EncounterNoteMedicationRow {
  clientId: string;
  id?: string;
  medication: string;
  dosage: string;
  frequency: string;
  frequencyOther: boolean;
  dateStarted: string | null;
  dateStopped: string | null;
  isDateStoppedNA: boolean;
  medicationConcept?: FormCodeableConcept;
  sourceDate?: string;
  confirmedAt?: string;
  confirmedBy?: string;
}

export interface EncounterNoteDiagnosisFormValue {
  id?: string;
  text: string;
  code: string;
  concept?: FormCodeableConcept;
  recordedDate?: string;
}

export interface EncounterNoteFormValues {
  startTime: string;
  endTime: string;
  visitReason: string;
  assessment: string;
  hpi: string;
  treatmentPlan: string;
  vitalSigns: string;
  reviewSymptoms: string;
  exam: string;
  noteForPatient: string;
  noteForReferring: string;
  conditions: EncounterNoteConditionRow[];
  medications: EncounterNoteMedicationRow[];
  diagnoses: EncounterNoteDiagnosisFormValue[];
  suspectedPositionalApnea: string;
}

export interface EncounterNoteAddendum {
  id?: string;
  authorName: string;
  timestamp: string;
  text: string;
}
